/**
 * Offline Mutation Queue with Exponential Backoff
 *
 * Stores failed mutations in localStorage and retries them when back online.
 * Each mutation is stored with metadata for retry logic.
 */

export interface QueuedMutation {
  id: string;
  type: 'home_objects' | 'world_config' | 'story_progress' | 'item_instance';
  payload: any;
  createdAt: number;
  retryCount: number;
  lastRetryAt?: number;
  userId: string;
  childId?: number | null;
}

const QUEUE_STORAGE_KEY = 'lilhero_sync_queue';
const MAX_RETRIES = 5;
const BASE_DELAY_MS = 1000; // 1 second
const MAX_DELAY_MS = 60000; // 1 minute

/**
 * Calculate delay with exponential backoff
 */
const getBackoffDelay = (retryCount: number): number => {
  const delay = Math.min(BASE_DELAY_MS * Math.pow(2, retryCount), MAX_DELAY_MS);
  // Add jitter (±20%) to avoid thundering herd
  const jitter = delay * 0.2 * (Math.random() - 0.5);
  return Math.round(delay + jitter);
};

/**
 * Get all queued mutations from localStorage
 */
export const getQueuedMutations = (): QueuedMutation[] => {
  try {
    const stored = localStorage.getItem(QUEUE_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('❌ Error reading sync queue:', error);
    return [];
  }
};

/**
 * Save mutations queue to localStorage
 */
const saveQueue = (queue: QueuedMutation[]): void => {
  try {
    localStorage.setItem(QUEUE_STORAGE_KEY, JSON.stringify(queue));
  } catch (error) {
    console.error('❌ Error saving sync queue:', error);
  }
};

/**
 * Add a mutation to the queue
 */
export const enqueueMutation = (
  type: QueuedMutation['type'],
  payload: any,
  userId: string,
  childId?: number | null
): QueuedMutation => {
  const mutation: QueuedMutation = {
    id: `${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    type,
    payload,
    createdAt: Date.now(),
    retryCount: 0,
    userId,
    childId
  };

  const queue = getQueuedMutations();

  // For 'home_objects' and 'world_config', replace existing mutation (last-write-wins)
  if (type === 'home_objects' || type === 'world_config') {
    const filteredQueue = queue.filter(
      m => !(m.type === type && m.userId === userId && m.childId === childId)
    );
    filteredQueue.push(mutation);
    saveQueue(filteredQueue);
  } else {
    // For other types, append
    queue.push(mutation);
    saveQueue(queue);
  }

  console.log(`📥 Mutation enqueued: ${type} (${mutation.id})`);
  return mutation;
};

/**
 * Remove a mutation from the queue
 */
export const dequeueMutation = (id: string): void => {
  const queue = getQueuedMutations();
  const filtered = queue.filter(m => m.id !== id);
  saveQueue(filtered);
  console.log(`✅ Mutation dequeued: ${id}`);
};

/**
 * Update mutation retry info
 */
export const updateMutationRetry = (id: string): QueuedMutation | null => {
  const queue = getQueuedMutations();
  const index = queue.findIndex(m => m.id === id);

  if (index === -1) return null;

  queue[index].retryCount += 1;
  queue[index].lastRetryAt = Date.now();

  // Remove if max retries exceeded
  if (queue[index].retryCount > MAX_RETRIES) {
    console.warn(`⚠️ Mutation ${id} exceeded max retries, removing`);
    queue.splice(index, 1);
    saveQueue(queue);
    return null;
  }

  saveQueue(queue);
  return queue[index];
};

/**
 * Get mutations ready for retry (respecting backoff)
 */
export const getMutationsReadyForRetry = (userId: string): QueuedMutation[] => {
  const queue = getQueuedMutations();
  const now = Date.now();

  return queue.filter(m => {
    if (m.userId !== userId) return false;
    if (m.retryCount === 0) return true; // New mutations
    if (!m.lastRetryAt) return true;

    const delay = getBackoffDelay(m.retryCount);
    return (now - m.lastRetryAt) >= delay;
  });
};

/**
 * Clear all mutations for a user (used on logout)
 */
export const clearUserMutations = (userId: string): void => {
  const queue = getQueuedMutations();
  const filtered = queue.filter(m => m.userId !== userId);
  saveQueue(filtered);
  console.log(`🗑️ Cleared sync queue for user ${userId}`);
};

/**
 * Check if there are pending mutations
 */
export const hasPendingMutations = (userId: string): boolean => {
  const queue = getQueuedMutations();
  return queue.some(m => m.userId === userId);
};

/**
 * Get queue statistics
 */
export const getQueueStats = (userId: string): { pending: number; failed: number } => {
  const queue = getQueuedMutations();
  const userQueue = queue.filter(m => m.userId === userId);

  return {
    pending: userQueue.filter(m => m.retryCount === 0).length,
    failed: userQueue.filter(m => m.retryCount > 0).length
  };
};
